// Use environment variable or fallback to deployed backend
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://lectureai-backend.fly.dev';
const API_URL = `${BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('session_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const api = {
  // Settings
  getSettings: async () => {
    const response = await fetch(`${API_URL}/settings`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch settings');
    return response.json();
  },

  updateSettings: async (data) => {
    const response = await fetch(`${API_URL}/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update settings');
    }
    return response.json();
  },

  // Usage
  getUsage: async () => {
    const response = await fetch(`${API_URL}/usage`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch usage');
    return response.json();
  },

  getAiModelCatalog: async () => {
    const response = await fetch(`${API_URL}/ai/model-catalog`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch AI model catalog');
    return response.json();
  },

  // Library documents
  getDocuments: async () => {
    const response = await fetch(`${API_URL}/documents`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch documents');
    return response.json();
  },

  getDocument: async (documentId) => {
    const response = await fetch(`${API_URL}/documents/${documentId}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch document');
    return response.json();
  },

  createDocument: async (data) => {
    const response = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create document');
    }
    return response.json();
  },

  createTextDocument: async (data) => {
    const response = await fetch(`${API_URL}/documents/text`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create text document');
    }
    return response.json();
  },

  updateDocument: async (documentId, data) => {
    const response = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update document');
    }
    return response.json();
  },

  deleteDocument: async (documentId) => {
    const response = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete document');
    }
    return response.json();
  },

  completeDocumentUpload: async (documentId, data) => {
    const response = await fetch(`${API_URL}/documents/${documentId}/upload-complete`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to complete document upload');
    }
    return response.json();
  },

  extractDocumentText: async (documentId) => {
    const response = await fetch(`${API_URL}/documents/${documentId}/extract-text`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to extract document text');
    }
    return response.json();
  },

  getMasteryJourneys: async () => {
    const response = await fetch(`${API_URL}/mastery-journeys`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch mastery journeys');
    return response.json();
  },

  getSubjectMasteryJourney: async (subjectId) => {
    const response = await fetch(`${API_URL}/subjects/${subjectId}/mastery-journey`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch mastery journey');
    }
    return response.json();
  },

  refreshSubjectMasteryJourney: async (subjectId, model = null) => {
    const response = await fetch(`${API_URL}/subjects/${subjectId}/mastery-journey/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ model })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to refresh mastery journey');
    }
    return response.json();
  },

  executeAgentTool: async (toolId, payload = {}, surface = null) => {
    const response = await fetch(`${API_URL}/agent-tools/execute`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({
        tool_id: toolId,
        surface,
        payload,
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to execute agent tool');
    }
    return response.json();
  },

  // Lectures
  getLectures: async () => {
    const response = await fetch(`${API_URL}/lectures`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch lectures');
    return response.json();
  },

  getLecture: async (id) => {
    const response = await fetch(`${API_URL}/lectures/${id}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch lecture');
    return response.json();
  },

  createLecture: async (data) => {
    const response = await fetch(`${API_URL}/lectures`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create lecture');
    }
    return response.json();
  },

  updateLecture: async (id, data) => {
    const response = await fetch(`${API_URL}/lectures/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update lecture');
    }
    return response.json();
  },

  deleteLecture: async (id) => {
    const response = await fetch(`${API_URL}/lectures/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete lecture');
    return response.json();
  },

  // Transcription
  transcribe: async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/transcribe`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Transcription failed');
    }
    return response.json();
  },

  // Transcribe a single chunk for lazy/real-time transcription
  transcribeChunk: async (audioBlob, chunkIndex) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, `chunk_${chunkIndex}.webm`);
    formData.append('chunk_index', chunkIndex.toString());
    
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/transcribe-chunk?chunk_index=${chunkIndex}`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    
    if (!response.ok) {
      return { text: '', chunk_index: chunkIndex, duration: 0, cost: 0 };
    }
    return response.json();
  },

  // Generate live summary during recording (lightweight, for real-time feedback)
  generateLiveSummary: async (text, previousSummary = null) => {
    const response = await fetch(`${API_URL}/live-summary`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ text, previous_summary: previousSummary })
    });
    
    if (!response.ok) {
      return { summary: '', cost: 0 };
    }
    return response.json();
  },

  // Format lecture
  formatLecture: async (transcript, model) => {
    const response = await fetch(`${API_URL}/format-lecture`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ transcript, model })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Formatting failed');
    }
    return response.json();
  },

  // Format lecture with streaming for real-time display
  formatLectureStream: async (transcript, model, onChunk, onComplete, onError) => {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/format-lecture-stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ transcript, model })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Formatting failed');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          onChunk(data);
        } else if (line.startsWith('event: done')) {
          // Next line has the cost
        } else if (line.startsWith('event: error')) {
          // Next line has the error
        }
      }
    }

    if (onComplete) onComplete();
  },

  // Chat - supports single lecture (lectureId) or multiple (lectureIds)
  chat: async (messages, model, lectureId, contextDocuments = [], contextUrls = [], lectureIds = [], assistantMode = 'tutor') => {
    const response = await fetch(`${API_URL}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ 
        messages, 
        model, 
        lecture_id: lectureId,
        lecture_ids: lectureIds.length > 0 ? lectureIds : undefined,
        context_documents: contextDocuments,
        context_urls: contextUrls,
        assistant_mode: assistantMode
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Chat failed');
    }
    return response.json();
  },

  // Learn-assist™ Tools
  learnAssist: async (toolId, topic, userExplanation = null, lectureIds = [], model = null, assistantMode = 'tutor') => {
    const response = await fetch(`${API_URL}/learn-assist`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ 
        tool_id: toolId,
        topic,
        user_explanation: userExplanation,
        lecture_ids: lectureIds.length > 0 ? lectureIds : undefined,
        model,
        assistant_mode: assistantMode
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Learn-assist failed');
    }
    return response.json();
  },

  // Parse document
  parseDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/parse-document`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Document parsing failed');
    }
    return response.json();
  },

  // Fetch URL content
  fetchUrl: async (url) => {
    const response = await fetch(`${API_URL}/fetch-url?url=${encodeURIComponent(url)}`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'URL fetch failed');
    }
    return response.json();
  },

  // Subjects
  getSubjects: async () => {
    const response = await fetch(`${API_URL}/subjects`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch subjects');
    return response.json();
  },

  createSubject: async (data) => {
    const response = await fetch(`${API_URL}/subjects`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create subject');
    }
    return response.json();
  },

  deleteSubject: async (id) => {
    const response = await fetch(`${API_URL}/subjects/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete subject');
    return response.json();
  },

  // AI Suggestions
  generateSuggestions: async (lectureId) => {
    const response = await fetch(`${API_URL}/generate-suggestions?lecture_id=${lectureId}`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to generate suggestions');
    }
    return response.json();
  },

  // Universities
  searchUniversities: async (query = '') => {
    const response = await fetch(`${API_URL}/universities?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to search universities');
    return response.json();
  },

  // Course Info
  getCourseInfo: async (university, courseCode) => {
    const response = await fetch(`${API_URL}/course-info?university=${encodeURIComponent(university)}&course_code=${encodeURIComponent(courseCode)}`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to get course info');
    return response.json();
  },

  // Update Subject
  updateSubject: async (id, data) => {
    const response = await fetch(`${API_URL}/subjects/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update subject');
    }
    return response.json();
  },

  // Review Sessions
  getReviewSessions: async () => {
    const response = await fetch(`${API_URL}/reviews`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch review sessions');
    return response.json();
  },

  createReviewSession: async (data) => {
    const response = await fetch(`${API_URL}/reviews`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create review session');
    }
    return response.json();
  },

  getReviewSession: async (id) => {
    const response = await fetch(`${API_URL}/reviews/${id}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch review session');
    return response.json();
  },

  generateQuestion: async (sessionId, data = {}) => {
    const response = await fetch(`${API_URL}/reviews/${sessionId}/generate-question`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to generate question');
    }
    return response.json();
  },

  submitAnswer: async (sessionId, questionIndex, userAnswer, options = {}) => {
    const response = await fetch(`${API_URL}/reviews/${sessionId}/submit-answer`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({
        question_index: questionIndex,
        user_answer: userAnswer,
        ...options,
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit answer');
    }
    return response.json();
  },

  createReviewNote: async (data) => {
    const response = await fetch(`${API_URL}/review-notes`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create review note');
    }
    return response.json();
  },

  deleteReviewSession: async (id) => {
    const response = await fetch(`${API_URL}/reviews/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete review session');
    return response.json();
  },

  updateReviewSession: async (id, data) => {
    const response = await fetch(`${API_URL}/reviews/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update review session');
    }
    return response.json();
  },

  // Confusion Logs (Learning Analytics)
  createConfusionLog: async (data) => {
    const response = await fetch(`${API_URL}/confusion-logs`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create confusion log');
    }
    return response.json();
  },

  updateConfusionLog: async (id, data) => {
    const response = await fetch(`${API_URL}/confusion-logs/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update confusion log');
    }
    return response.json();
  },

  getConfusionLogs: async (limit = 50) => {
    const response = await fetch(`${API_URL}/confusion-logs?limit=${limit}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch confusion logs');
    return response.json();
  },

  // Learning Insights (Analytics)
  getLearningInsights: async () => {
    const response = await fetch(`${API_URL}/learning-insights`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch learning insights');
    return response.json();
  },

  getAgentTools: async (surface = null) => {
    const params = new URLSearchParams();
    if (surface) params.append('surface', surface);
    const response = await fetch(`${API_URL}/agent-tools${params.toString() ? `?${params.toString()}` : ''}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch agent tools');
    return response.json();
  },

  // Voice Configuration
  getVoices: async () => {
    const response = await fetch(`${API_URL}/voices`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch voices');
    return response.json();
  },

  synthesizeSpeech: async (data) => {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/speech/synthesize`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let detail = 'Failed to synthesize speech';
      try {
        const error = await response.json();
        detail = error.detail || detail;
      } catch (_) {
        // ignore non-json error bodies
      }
      throw new Error(detail);
    }
    return response.blob();
  },

  // Realtime WebSocket URL
  getRealtimeWsUrl: () => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = BACKEND_URL.replace(/^https?:/, wsProtocol);
    return `${baseUrl}/api/realtime/ws`;
  },

  // Avatar API
  getAvatarCurrent: async (timezoneOffset = null) => {
    const params = new URLSearchParams();
    if (timezoneOffset !== null) {
      params.append('timezone_offset', timezoneOffset.toString());
    }
    const url = `${API_URL}/avatar/current${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get avatar');
    }
    return response.json();
  },

  getAvatarFeeling: async (text, mood = null, timezoneOffset = null) => {
    const response = await fetch(`${API_URL}/avatar/feeling`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({
        text,
        mood,
        timezone_offset: timezoneOffset
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get personalized avatar');
    }
    return response.json();
  },

  generateWeeklyAvatars: async (weeklySetId = null, moods = null, forceRegenerate = false) => {
    const response = await fetch(`${API_URL}/avatar/admin/generate-weekly`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({
        weekly_set_id: weeklySetId,
        moods,
        force_regenerate: forceRegenerate
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to generate weekly avatars');
    }
    return response.json();
  },

  // ============================================
  // EXAM SCHEDULING
  // ============================================

  getExams: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.upcomingOnly) params.append('upcoming_only', 'true');
    if (options.pastOnly) params.append('past_only', 'true');
    if (options.subjectId) params.append('subject_id', options.subjectId);
    
    const url = `${API_URL}/exams${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch exams');
    return response.json();
  },

  getExam: async (id) => {
    const response = await fetch(`${API_URL}/exams/${id}`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch exam');
    return response.json();
  },

  createExam: async (data) => {
    const response = await fetch(`${API_URL}/exams`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create exam');
    }
    return response.json();
  },

  updateExam: async (id, data) => {
    const response = await fetch(`${API_URL}/exams/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update exam');
    }
    return response.json();
  },

  deleteExam: async (id) => {
    const response = await fetch(`${API_URL}/exams/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete exam');
    return response.json();
  },

  importICS: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/exams/import-ics`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to import ICS file');
    }
    return response.json();
  },

  // ============================================
  // EXAM SCORES
  // ============================================

  submitExamScore: async (examId, score, maxScore = 100, notes = null) => {
    const response = await fetch(`${API_URL}/exams/${examId}/score`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ score, max_score: maxScore, notes })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit exam score');
    }
    return response.json();
  },

  getExamScore: async (examId) => {
    const response = await fetch(`${API_URL}/exams/${examId}/score`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch exam score');
    }
    return response.json();
  },

  // ============================================
  // STUDY PRIORITIES & DASHBOARD
  // ============================================

  getStudyPriorities: async () => {
    const response = await fetch(`${API_URL}/study/priorities`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch study priorities');
    return response.json();
  },

  getStudyRecommendation: async () => {
    const response = await fetch(`${API_URL}/study/recommendation`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch study recommendation');
    return response.json();
  },

  getStudyDashboard: async () => {
    const response = await fetch(`${API_URL}/study/dashboard`, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch study dashboard');
    return response.json();
  },

  // ============================================
  // STUDY SESSION TRACKING
  // ============================================

  startStudySession: async (subjectId = null, source = 'manual') => {
    const response = await fetch(`${API_URL}/study/sessions/start`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ subject_id: subjectId, source })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start study session');
    }
    return response.json();
  },

  endStudySession: async (sessionId, durationMinutes = null) => {
    const response = await fetch(`${API_URL}/study/sessions/${sessionId}/end`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ duration_minutes: durationMinutes })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end study session');
    }
    return response.json();
  },

  getStudySessions: async (subjectId = null, limit = 50) => {
    const params = new URLSearchParams();
    if (subjectId) params.append('subject_id', subjectId);
    params.append('limit', limit.toString());
    
    const url = `${API_URL}/study/sessions?${params.toString()}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch study sessions');
    return response.json();
  }
};
